import React, { createContext, useContext, useRef, useCallback } from 'react';
import BlockchainService from '../services/blockchainService';

const BlockchainServiceContext = createContext();

/**
 * Cache global para datos de blockchain con TTL
 */
class BlockchainCache {
  constructor() {
    this.cache = new Map();
  }

  set(key, data, ttlMs = 300000) { // Default 5 minutes TTL
    const expiry = Date.now() + ttlMs;
    this.cache.set(key, { data, expiry });
  }

  get(key) {
    const item = this.cache.get(key);
    if (!item) return null;
    
    if (Date.now() > item.expiry) {
      this.cache.delete(key);
      return null;
    }
    
    return item.data;
  }

  has(key) {
    const item = this.cache.get(key);
    if (!item) return false;
    
    if (Date.now() > item.expiry) {
      this.cache.delete(key);
      return false;
    }
    
    return true;
  }

  clear(pattern) {
    if (pattern) {
      // Clear keys matching pattern
      for (const key of this.cache.keys()) {
        if (key.includes(pattern)) {
          this.cache.delete(key);
        }
      }
    } else {
      // Clear all
      this.cache.clear();
    }
  }

  getStats() {
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;
    
    for (const [, item] of this.cache.entries()) {
      if (now > item.expiry) {
        expiredEntries++;
      } else {
        validEntries++;
      }
    }
    
    return { validEntries, expiredEntries, totalEntries: this.cache.size };
  }
}

// Singleton cache instance
const globalCache = new BlockchainCache();

export const BlockchainServiceProvider = ({ children }) => {
  const servicesRef = useRef({});
  
  const getService = useCallback((chainId) => {
    if (!servicesRef.current[chainId]) {
      servicesRef.current[chainId] = new BlockchainService(chainId);
    }
    return servicesRef.current[chainId];
  }, []);

  const clearServiceCache = useCallback((chainId) => {
    if (chainId) {
      globalCache.clear(chainId);
    } else {
      globalCache.clear();
    }
  }, []);

  const getCachedData = useCallback((key) => {
    return globalCache.get(key);
  }, []);

  const setCachedData = useCallback((key, data, ttlMs) => {
    globalCache.set(key, data, ttlMs);
  }, []);

  const hasCachedData = useCallback((key) => {
    return globalCache.has(key);
  }, []);

  const getCacheStats = useCallback(() => {
    return globalCache.getStats();
  }, []);

  const contextValue = {
    getService,
    clearServiceCache,
    getCachedData,
    setCachedData,
    hasCachedData,
    getCacheStats
  };

  return (
    <BlockchainServiceContext.Provider value={contextValue}>
      {children}
    </BlockchainServiceContext.Provider>
  );
};

export const useBlockchainService = () => {
  const context = useContext(BlockchainServiceContext);
  if (!context) {
    throw new Error('useBlockchainService must be used within a BlockchainServiceProvider');
  }
  return context;
};

export default BlockchainServiceContext;
